import React, { useMemo, useState } from "react";

import {

  previewCsvImport,

  replaceOfficialKeepDemo,

} from "@/utils/csvImport.js";

import {

  getImportContext,

  replaceCollection,

  mergeCollection,

  resetDataStore,

} from "@/data/dataStore.js";

import { notifyDataUpdate, useDataStore } from "./useDataStore.js";

import { DataTable } from "@/components/DataTable.jsx";

import { useKarateUi } from "@/hooks/useKarateUi.js";



/**

 * @param {'dojos'|'instructeurs'|'etudiants'} type

 */

export function ImportCsvPanel({ type: initialType = "dojos" }) {

  const store = useDataStore();

  const { ui, importModeLabel } = useKarateUi();

  const imp = ui.import;

  const [type, setType] = useState(initialType);

  const [csvText, setCsvText] = useState("");

  const [mode, setMode] = useState("replace_official");

  const [message, setMessage] = useState("");

  const [showPreview, setShowPreview] = useState(false);



  const context = useMemo(

    () => ({

      dojoIds: store.dojos.map((d) => d.id),

      instructeurIds: store.instructeurs.map((i) => i.id),

    }),

    [store],

  );



  const preview = useMemo(() => {

    if (!showPreview || !csvText.trim()) return null;

    return previewCsvImport(type, csvText, context);

  }, [showPreview, csvText, type, context]);



  const collectionKey =

    type === "dojos" ? "dojos" : type === "instructeurs" ? "instructeurs" : "etudiants";



  const importModes = useMemo(

    () => ({

      replace: importModeLabel("replace"),

      merge: importModeLabel("merge"),

      replace_official: importModeLabel("replace_official"),

    }),

    [importModeLabel],

  );



  const handlePreview = () => {

    setShowPreview(true);

    setMessage("");

  };



  const handleImport = () => {

    const result = previewCsvImport(type, csvText, getImportContext());

    if (!result.valid) {

      setMessage(imp.importRefused(result.errors.join(" ")));

      setShowPreview(true);

      return;

    }

    const existing = store[collectionKey] || [];

    if (mode === "replace") {

      replaceCollection(collectionKey, result.rows);

    } else if (mode === "merge") {

      mergeCollection(collectionKey, result.rows);

    } else {

      replaceCollection(collectionKey, replaceOfficialKeepDemo(existing, result.rows));

    }

    notifyDataUpdate();

    setMessage(

      imp.importSuccess(

        result.rows.length,

        result.stats.official,

        result.stats.demo,

        importModes[mode],

      ),

    );

  };



  const handleReset = () => {

    resetDataStore();

    notifyDataUpdate();

    setCsvText("");

    setShowPreview(false);

    setMessage(imp.resetSuccess);

  };



  const previewColumns = useMemo(() => {

    if (type === "etudiants") {

      return [

        { key: "id", label: ui.common.id },

        { key: "prenom", label: ui.columns.firstName },

        { key: "nom", label: ui.columns.name },

        { key: "dojo_id", label: ui.columns.dojo },

        { key: "demo", label: ui.common.demo, render: (r) => (r.demo ? ui.common.yes : ui.common.no) },

      ];

    }

    if (type === "instructeurs") {

      return [

        { key: "id", label: ui.common.id },

        { key: "nom", label: ui.columns.name },

        { key: "titre", label: ui.columns.title },

        { key: "dojo_id", label: ui.columns.dojo },

        { key: "demo", label: ui.common.demo, render: (r) => (r.demo ? ui.common.yes : ui.common.no) },

      ];

    }

    return [

      { key: "id", label: ui.common.id },

      { key: "nom", label: ui.columns.name },

      { key: "ville", label: ui.columns.city },

      { key: "sensei_id", label: ui.columns.title },

      { key: "demo", label: ui.common.demo, render: (r) => (r.demo ? ui.common.yes : ui.common.no) },

    ];

  }, [type, ui]);



  return (

    <div className="import-panel" data-testid="import-csv-panel">

      <div className="info-box">

        <strong>{imp.orderNote}</strong>

        <br />

        {imp.templates} : <code>00_PILOTAGE/templates/README-IMPORT-CSV-KARATE.md</code>

      </div>



      <label>

        {imp.dataType}

        <select value={type} onChange={(e) => { setType(e.target.value); setShowPreview(false); }} data-testid="import-type">

          <option value="dojos">{imp.typeDojos}</option>

          <option value="instructeurs">{imp.typeInstructors}</option>

          <option value="etudiants">{imp.typeStudents}</option>

        </select>

      </label>



      <label>

        {imp.importMode}

        <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="import-mode">

          {Object.entries(importModes).map(([k, label]) => (

            <option key={k} value={k}>{label}</option>

          ))}

        </select>

      </label>



      <label>

        {imp.csvContent}

        <textarea

          value={csvText}

          onChange={(e) => { setCsvText(e.target.value); setShowPreview(false); }}

          rows={10}

          placeholder={imp.csvPlaceholder}

          data-testid="import-textarea"

        />

      </label>



      <div className="btn-row">

        <button type="button" className="btn-secondary" onClick={handlePreview} data-testid="import-preview-btn">

          {imp.previewValidate}

        </button>

        <button

          type="button"

          className="btn-primary"

          onClick={handleImport}

          disabled={!csvText.trim()}

          data-testid="import-submit"

        >

          {imp.confirmImport}

        </button>

        <button type="button" className="btn-secondary" onClick={handleReset} data-testid="import-reset">

          {imp.resetDemo}

        </button>

      </div>



      {preview ? (

        <div className="import-preview" data-testid="import-preview-panel">

          <h3>{imp.previewTitle}</h3>

          {preview.valid ? (

            <p className="preview-ok" data-testid="import-preview-valid">

              {imp.previewOk(preview.stats.total, preview.stats.official, preview.stats.demo)}

            </p>

          ) : (

            <ul className="preview-errors" data-testid="import-preview-errors">

              {preview.errors.map((e) => (

                <li key={e}>{e}</li>

              ))}

            </ul>

          )}

          {preview.warnings.length > 0 ? (

            <ul className="preview-warnings" data-testid="import-preview-warnings">

              {preview.warnings.map((w) => (

                <li key={w}>{w}</li>

              ))}

            </ul>

          ) : null}

          {preview.rows.length > 0 ? (

            <DataTable

              rows={preview.rows}

              columns={previewColumns}

              searchFields={["id", "nom", "prenom", "ville"]}

              testId="import-preview-table"

            />

          ) : null}

        </div>

      ) : null}



      {message ? (

        <p className="import-message" data-testid="import-message">{message}</p>

      ) : null}

    </div>

  );

}


